import React from 'react';
import { useNavigate } from 'react-router-dom';
import SEOMeta from './SEOMeta';
import { Button } from './ui/button';
import PublicNavbar from './PublicNavbar';
import {
  MessageSquare,
  Shield,
  Clock,
  TrendingUp,
  CheckCircle,
  Sparkles,
  Wrench,
  Bell,
  ArrowRight,
  Users,
  Zap,
  Brain,
  Bot,
  Workflow,
  FileText,
  CreditCard,
  BarChart3,
  Phone,
  Mail,
  Calendar,
  Search,
  Settings,
  Lock,
  MessageCircle,
  ClipboardCheck,
  BarChart2,
  PenLine,
  ListTodo,
  Headphones,
  GraduationCap,
  Star,
  Cpu,
  Activity,
} from 'lucide-react';

const FeaturesPage = () => {
  const navigate = useNavigate();

  const coreFeatures = [
    {
      icon: Bot,
      title: '24/7 AI Agent',
      description: 'Your AI property manager never sleeps. Handle tenant inquiries, maintenance requests, and emergency situations around the clock with instant, professional responses.',
      color: 'cyan',
      benefits: ['Instant response times', 'Professional communication', 'Consistent tenant experience']
    },
    {
      icon: Wrench,
      title: 'Smart Maintenance Coordination',
      description: 'Automatically categorize, prioritize, and dispatch maintenance requests. Our AI coordinates with your vendor network to get issues resolved faster.',
      color: 'blue',
      benefits: ['Auto-dispatch to vendors', 'Priority-based routing', 'Real-time status updates']
    },
    {
      icon: MessageSquare,
      title: 'Conversational Tenant Portal',
      description: 'Tenants can report issues, ask questions, and get updates through natural conversation. No more phone tag or missed emails.',
      color: 'green',
      benefits: ['Natural language interface', 'Multi-channel support', 'Automated follow-ups']
    },
    {
      icon: Brain,
      title: 'Predictive Intelligence',
      description: 'Move from reactive to proactive management. Our AI identifies patterns and predicts issues before they become costly problems.',
      color: 'purple',
      benefits: ['Predictive maintenance', 'Lease renewal forecasting', 'Risk assessment']
    },
    {
      icon: Workflow,
      title: 'Automated Workflows',
      description: 'From move-in to move-out, automate every step. Lease reminders, inspection scheduling, and renewal processing happen automatically.',
      color: 'orange',
      benefits: ['Move-in/move-out automation', 'Lease management', 'Inspection scheduling']
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Bank-level encryption, SOC 2 compliance in progress, and data handling designed specifically for property managers.',
      color: 'pink',
      benefits: ['256-bit encryption', 'Role-based access', 'Audit trails']
    }
  ];

  const additionalFeatures = [
    { icon: FileText, title: 'Document Management', description: 'Store and organize leases, inspection reports, and tenant documents in one secure place.' },
    { icon: CreditCard, title: 'Payment Tracking', description: 'Monitor rent payments, track late fees, and generate financial reports automatically.' },
    { icon: BarChart3, title: 'Analytics Dashboard', description: 'Real-time insights into portfolio performance, occupancy rates, and maintenance costs.' },
    { icon: Phone, title: 'Multi-Channel Communication', description: 'Reach tenants via SMS, email, or in-app messaging, all from one unified inbox.' },
    { icon: Calendar, title: 'Smart Scheduling', description: 'AI-powered scheduling for inspections, showings, and maintenance visits.' },
    { icon: Search, title: 'Tenant Screening', description: 'Streamlined application processing with integrated background and credit checks.' },
    { icon: Users, title: 'Vendor Network', description: 'Built-in vendor directory with ratings, availability, and automatic dispatching.' },
    { icon: Settings, title: 'Custom Workflows', description: 'Configure automation rules to match your specific property management style.' },
    { icon: Lock, title: 'Compliance Tools', description: 'Stay compliant with local regulations and fair housing requirements.' }
  ];

  const getColorClasses = (color) => {
    const colors = {
      cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', border: 'border-cyan-300' },
      blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-300' },
      green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-300' },
      purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-300' },
      orange: { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-300' },
      pink: { bg: 'bg-pink-100', text: 'text-pink-600', border: 'border-pink-300' }
    };
    return colors[color] || colors.cyan;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEOMeta
        title="Features: AI Maintenance, Tenant Communication & Vendor Dispatch"
        description="Explore NivasaPro's agentic AI features: automatic maintenance triage, 24/7 tenant chat, smart vendor matching, owner approval workflows, and more."
        canonical="/features"
      />
      {/* Navigation */}
      <PublicNavbar activeRoute="/features" />

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm font-semibold tracking-wide text-cyan-700 uppercase mb-4">
            Comprehensive Feature Set
          </p>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-gray-900 mb-6">
            Everything You Need to{' '}
            <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              Manage Properties Smarter
            </span>
          </h1>

          <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
            NivasaPro.ai combines cutting-edge AI with practical property management tools.
            From automated tenant communication to predictive maintenance, we've built
            every feature landlords actually need.
          </p>

          <Button
            size="lg"
            className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white px-8 py-6 text-lg"
            onClick={() => navigate('/')}
          >
            Get Early Access <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Core Features */}
      <section className="py-16 md:py-24 bg-white border-y">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Core AI-Powered Features
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The intelligence engine that transforms how you manage properties
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreFeatures.map((feature, index) => {
              const colors = getColorClasses(feature.color);
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className={`p-6 rounded-xl border border-gray-200 hover:${colors.border} hover:shadow-lg transition-all bg-white`}
                >
                  <div className={`w-14 h-14 ${colors.bg} rounded-xl flex items-center justify-center mb-5`}>
                    <Icon className={`h-7 w-7 ${colors.text}`} />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600 mb-4">{feature.description}</p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center text-sm text-gray-700">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Here's how NivasaPro.ai handles a typical maintenance request
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Mock Chat Interface */}
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-2xl blur-2xl opacity-20"></div>
              <div className="relative bg-white rounded-2xl shadow-2xl p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-6 pb-4 border-b">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center">
                      <Sparkles className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">NivasaPro AI Agent</div>
                      <div className="text-xs text-green-600 flex items-center">
                        <div className="w-2 h-2 bg-green-600 rounded-full mr-1"></div>
                        Active
                      </div>
                    </div>
                  </div>
                  <MessageSquare className="h-5 w-5 text-gray-400" />
                </div>

                <div className="space-y-4">
                  {/* Tenant Message */}
                  <div className="flex justify-end">
                    <div className="bg-gray-100 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%]">
                      <p className="text-sm text-gray-800">
                        Hi, there's a pipe leak under my kitchen sink. Water is dripping slowly.
                      </p>
                      <p className="text-xs text-gray-500 mt-1">2:34 PM</p>
                    </div>
                  </div>

                  {/* AI Response */}
                  <div className="flex justify-start">
                    <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%]">
                      <p className="text-sm text-white">
                        Thanks for reporting this, Sarah. I've documented the issue and am coordinating with our plumber partner.
                        Expected resolution: within 24 hours. I'll keep you updated!
                      </p>
                      <div className="flex items-center space-x-2 mt-2">
                        <Wrench className="h-4 w-4 text-cyan-100" />
                        <span className="text-xs text-cyan-100">Maintenance scheduled</span>
                      </div>
                      <p className="text-xs text-cyan-100 mt-1">2:35 PM</p>
                    </div>
                  </div>

                  {/* Status Update */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-green-900">Plumber dispatched</p>
                      <p className="text-xs text-green-700">ETA: Tomorrow 10 AM - Mike's Plumbing</p>
                    </div>
                  </div>

                  {/* Landlord Notification */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center space-x-3">
                    <Bell className="h-5 w-5 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Owner notified</p>
                      <p className="text-xs text-blue-700">Summary sent to your dashboard</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Features Grid */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              And So Much More
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A complete toolkit for modern property management
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {additionalFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="p-5 rounded-xl border border-gray-200 bg-white hover:border-cyan-300 hover:shadow-md transition-all flex items-start space-x-4"
                >
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{feature.title}</h3>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ROMI Score CTA */}
      <section className="py-16 bg-gradient-to-r from-gray-900 via-cyan-950 to-gray-900 border-y border-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-cyan-900/40 border border-cyan-700/40 text-cyan-300 text-xs font-semibold px-4 py-2 rounded-full mb-6">
              <Activity className="h-3.5 w-3.5" />
              Free · 3 minutes · No signup required
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Curious how mature your operation really is?
            </h2>
            <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
              The NivasaPro ROMI Score™ evaluates your operation across multiple dimensions and derives
              a maturity level unique to your portfolio. Free, instant, and honest.
              Get your personalised report in under 4 minutes.
            </p>
            <Button
              size="lg"
              className="bg-cyan-600 hover:bg-cyan-500 text-white px-8 py-6 text-base font-semibold"
              onClick={() => navigate('/romi-score')}
            >
              Learn About the ROMI Score™ <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="text-gray-600 text-sm mt-4">
              Used by landlords managing 1–50+ units · Results tailored to your portfolio
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-24 bg-white border-t">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-gray-600 max-w-xl mx-auto">
              Start free. Scale as you grow. No setup fees, no long-term contracts.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto items-stretch">

            {/* Free */}
            <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-3">Free</p>
              <div className="mb-1"><span className="text-4xl font-bold text-gray-900">$0</span></div>
              <p className="text-sm text-gray-500 mb-6">Up to 2 units · forever free</p>
              <ul className="space-y-3 text-sm text-gray-600 flex-1 mb-8">
                <li className="flex items-center gap-2.5"><Wrench className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>Maintenance tracking &amp; tenant portal</span></li>
                <li className="flex items-center gap-2.5"><Zap className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>50 Niva AI messages / month</span></li>
                <li className="flex items-center gap-2.5"><MessageSquare className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>Web chat only</span></li>
                <li className="flex items-center gap-2.5"><MessageCircle className="h-4 w-4 text-gray-300 flex-shrink-0" /><span className="text-gray-400">No SMS or WhatsApp</span></li>
                <li className="flex items-center gap-2.5"><ClipboardCheck className="h-4 w-4 text-gray-300 flex-shrink-0" /><span className="text-gray-400">No owner approvals</span></li>
              </ul>
              <Button variant="outline" className="w-full border-gray-300 text-gray-700 hover:bg-gray-50" onClick={() => navigate('/register')}>
                Get started free
              </Button>
            </div>

            {/* Starter — highlighted */}
            <div className="flex flex-col rounded-2xl border-2 border-cyan-500 bg-white p-6 shadow-lg relative">
              <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-cyan-500 text-white text-xs font-semibold px-4 py-1 rounded-full whitespace-nowrap">Most popular</span>
              <p className="text-xs font-semibold tracking-widest text-cyan-600 uppercase mb-3">Starter</p>
              <div className="mb-1 flex items-end gap-1">
                <span className="text-4xl font-bold text-gray-900">$29</span>
                <span className="text-sm text-gray-500 mb-1.5">/ mo</span>
              </div>
              <p className="text-sm text-gray-500 mb-6">+ $2 / unit · up to 10 units</p>
              <ul className="space-y-3 text-sm text-gray-600 flex-1 mb-8">
                <li className="flex items-center gap-2.5"><Zap className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>Unlimited Niva AI messages</span></li>
                <li className="flex items-center gap-2.5"><MessageCircle className="h-4 w-4 text-green-500 flex-shrink-0" /><span>WhatsApp channel for tenants</span></li>
                <li className="flex items-center gap-2.5"><Wrench className="h-4 w-4 text-orange-500 flex-shrink-0" /><span>AI triage &amp; vendor dispatch</span></li>
                <li className="flex items-center gap-2.5"><ClipboardCheck className="h-4 w-4 text-blue-500 flex-shrink-0" /><span>Owner approvals &amp; cost reporting</span></li>
                <li className="flex items-center gap-2.5"><PenLine className="h-4 w-4 text-violet-500 flex-shrink-0" /><span>AI-aided communication drafting</span></li>
                <li className="flex items-center gap-2.5"><ListTodo className="h-4 w-4 text-sky-500 flex-shrink-0" /><span>Niva work tracking &amp; reminders</span></li>
                <li className="flex items-center gap-2.5"><Headphones className="h-4 w-4 text-gray-400 flex-shrink-0" /><span>Standard customer support</span></li>
              </ul>
              <Button className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white" onClick={() => navigate('/register')}>
                Request access
              </Button>
            </div>

            {/* Professional */}
            <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-3">Professional</p>
              <div className="mb-1 flex items-end gap-1">
                <span className="text-4xl font-bold text-gray-900">$79</span>
                <span className="text-sm text-gray-500 mb-1.5">/ mo</span>
              </div>
              <p className="text-sm text-gray-500 mb-6">+ $3 / unit · up to 50 units</p>
              <ul className="space-y-3 text-sm text-gray-600 flex-1 mb-8">
                <li className="flex items-center gap-2.5"><CheckCircle className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>Everything in Starter</span></li>
                <li className="flex items-center gap-2.5"><Phone className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>SMS &amp; WhatsApp channels</span></li>
                <li className="flex items-center gap-2.5"><Bot className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>Niva AI for vendor communication</span></li>
                <li className="flex items-center gap-2.5"><Sparkles className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>Agentic vendor selection &amp; assignment</span></li>
                <li className="flex items-center gap-2.5"><BarChart3 className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>Advanced reporting &amp; SLA tracking</span></li>
                <li className="flex items-center gap-2.5"><GraduationCap className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>Dedicated onboarding support</span></li>
                <li className="flex items-center gap-2.5"><Star className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>White glove customer support</span></li>
              </ul>
              <Button variant="outline" className="w-full border-gray-300 text-gray-700 hover:bg-gray-50" onClick={() => navigate('/register')}>
                Request access
              </Button>
            </div>

            {/* Portfolio */}
            <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-semibold tracking-widest text-gray-400 uppercase mb-3">Portfolio</p>
              <div className="mb-1"><span className="text-4xl font-bold text-gray-900">Custom</span></div>
              <p className="text-sm text-gray-500 mb-6">50+ units · negotiated pricing</p>
              <ul className="space-y-3 text-sm text-gray-600 flex-1 mb-8">
                <li className="flex items-center gap-2.5"><CheckCircle className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>Everything in Professional</span></li>
                <li className="flex items-center gap-2.5"><Zap className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>API access &amp; SSO</span></li>
                <li className="flex items-center gap-2.5"><Cpu className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>Custom Niva tools</span></li>
                <li className="flex items-center gap-2.5"><Shield className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>White-label option</span></li>
                <li className="flex items-center gap-2.5"><Star className="h-4 w-4 text-cyan-500 flex-shrink-0" /><span>Dedicated support</span></li>
              </ul>
              <Button variant="outline" className="w-full border-gray-300 text-gray-700 hover:bg-gray-50" onClick={() => navigate('/register')}>
                Contact us
              </Button>
            </div>

          </div>

          <div className="mt-10 text-center">
            <p className="text-sm text-gray-500">
              🔥 <strong>Early access:</strong> Join now and lock in beta pricing for life.{' '}
              <button className="text-cyan-600 font-medium hover:underline" onClick={() => navigate('/register')}>
                Get on the list →
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* Central Ohio Focus */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-sm font-semibold tracking-wide text-orange-700 uppercase mb-3">
                  Local Focus
                </p>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Built for Central Ohio Landlords
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  We're not just another generic property management platform. NivasaPro.ai is
                  optimized specifically for the Central Ohio market with local knowledge built in.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-center text-gray-700">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                    Pre-vetted local vendor network
                  </li>
                  <li className="flex items-center text-gray-700">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                    Ohio-specific compliance knowledge
                  </li>
                  <li className="flex items-center text-gray-700">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                    Local market rent analysis
                  </li>
                  <li className="flex items-center text-gray-700">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                    Columbus-area support team
                  </li>
                </ul>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 border border-orange-200">
                <Users className="h-12 w-12 text-orange-600 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  From Landlords, For Landlords
                </h3>
                <p className="text-gray-600">
                  Built by property owners who've managed rentals in Central Ohio for over 20 years.
                  We understand your challenges because we've lived them.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-cyan-600 to-blue-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Transform Your Property Management?
          </h2>
          <p className="text-xl text-cyan-100 mb-8 max-w-2xl mx-auto">
            Join the waitlist and be among the first to experience AI-powered property management.
            Early adopters get 50% off for life.
          </p>
          <Button
            size="lg"
            className="bg-white text-cyan-700 hover:bg-gray-100 px-8 py-6 text-lg font-semibold"
            onClick={() => navigate('/')}
          >
            Get Early Access <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-6 w-6" />
                <span className="text-xl font-bold text-white">NivasaPro.ai</span>
              </div>
              <p className="text-sm text-gray-400">
                Agentic rental property management powered by AI.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/features')} className="hover:text-cyan-400 transition text-left">Features</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Pricing</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Security</button></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/about')} className="hover:text-cyan-400 transition text-left">About</button></li>
                <li><button onClick={() => navigate('/blog')} className="hover:text-cyan-400 transition text-left">Blog</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Careers</button></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/privacy')} className="hover:text-cyan-400 transition text-left">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-cyan-400 transition text-left">Terms of Service</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Contact</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2026 NivasaPro.ai. All rights reserved. Built for Central Ohio landlords.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FeaturesPage;
